import classNames from 'classnames';
import { observer } from 'mobx-react';
import { useEffect, useMemo } from 'react';
import Spinner from '../Spinner/Spinner';
import PaginationButton from './components/PaginationButton/PaginationButton';
import PaginationDirectionButton from './components/PaginationDirectionButton/PaginationDirectionButton';
import PaginationInfo from './components/PaginationInfo/PaginationInfo';
import { defaultPageItems, generatePageItems } from './helpers';
import './style.scss';
import { IPageItem, IPaginationProps, PaginationDirection, PaginationType } from './types';
import { LoadingStatus } from 'src/types/main';

// WARNING !!! Do not remove observer, refreshing does not work properly without it
const Pagination: React.FC<IPaginationProps> = observer(({ pagination, ...props }: IPaginationProps): JSX.Element => {
  const { type = PaginationType.TEXT, loading = LoadingStatus.Init, showPaginationInfo = false, showMorePageButtons = false } = props;
  const { data, current_page, last_page, refreshing: paginationRefreshing, loading: paginationLoading, total } = pagination;

  useEffect(() => {
    if (!data.length && current_page > 1) {
      props.onChange(current_page - 1);
    }
  }, [data, current_page]);

  const decrease = (index: number) => props.onChange(current_page - index);

  const increase = (index: number) => props.onChange(current_page + index);

  const isLoading = useMemo((): boolean => {
    return Boolean([LoadingStatus.Loading, LoadingStatus.Refreshing].includes(loading) || paginationRefreshing);
  }, [paginationRefreshing, loading]);

  /**
   * indicates whether to display pagination block
   */
  const isAvailablePagination = useMemo((): boolean => {
    if (props.hidden) return false;
    if ((paginationLoading || isLoading) && last_page === 1) return false;
    return Boolean(last_page !== 1 && total);
  }, [paginationLoading, isLoading, total, last_page, props.hidden]);

  const pageItems = useMemo((): IPageItem[] => {
    return showMorePageButtons ? generatePageItems(last_page, current_page, 9, true) : defaultPageItems(current_page);
  }, [showMorePageButtons, current_page, last_page]);

  return (
    <>
      {isAvailablePagination ? (
        <div className={classNames('pagination', props.className)}>
          {/* prev page button */}
          <PaginationDirectionButton
            direction={PaginationDirection.PREV}
            buttonType={type}
            disabled={current_page <= 1}
            changePage={() => decrease(1)}
          />
          {/* page number buttons */}
          {pageItems.map(({ value, label, isActive }) => (
            <PaginationButton
              key={`pagination-button-${value}`}
              value={value}
              label={label || value}
              isActive={isActive || current_page === value}
              lastPage={last_page}
              changePage={props.onChange}
            />
          ))}
          {/* next page button */}
          <PaginationDirectionButton
            direction={PaginationDirection.NEXT}
            buttonType={type}
            disabled={current_page >= last_page}
            changePage={() => increase(1)}
          />
          <PaginationInfo pagination={pagination} showPaginationInfo={showPaginationInfo} />
        </div>
      ) : null}

      {/* refreshing overlay */}
      {isLoading ? (
        <div className={classNames('scroll-list-refreshing d-flex', props.overlayClassName)}>
          <div className="d-flex flex-grow-1 justify-content-center m-auto">
            <Spinner />
          </div>
        </div>
      ) : null}
    </>
  );
});

export default Pagination;
